"use client";

/**
 * FoilGradeBadge — unified holographic grade badge.
 *
 * Single source of truth for Plan v2, Brand v2, and Niche v3 grade badges.
 *
 * Promoted (Session 43) from forks:
 *   - PlanShared.FoilGradeBadge          → sizes sm=56 / md=80 / lg=112, letter-grade only
 *   - BrandShared.FoilGradeBadge         → adds numeric score support ("7.4")
 *   - niche-v3/.../FoilGradeBadgeV2      → sizes 60 / 80 / 120, animateOnMount, fuller tier mapping
 *
 * Unified API supports BOTH old (sm/md/lg) and new (xs/sm/md/lg/xl) size aliases for
 * backward compatibility — old callers keep working without migration.
 *
 * Size mapping:
 *   xs → 48px  (mobile compact, list)
 *   sm → 60px  (cards — was 56 in Plan/Brand legacy, now 60 to match Niche v3)
 *   md → 80px  (grid card, scene secondary)
 *   lg → 120px (scene hero — was 112 in Plan/Brand legacy, now 120)
 *   xl → 160px (Wrapped Card 1 celebration)
 *
 * Tier classification (used for letter color + ring gradient + shadow):
 *   - "high"  : letter starts with S/A, OR numeric ≥ 7
 *   - "midA"  : letter starts with B,   OR numeric ≥ 5
 *   - "midB"  : letter starts with C
 *   - "low"   : everything else (D, invalid, numeric < 5)
 *
 * Reduced-motion: fade-in only (no scale/rotate spring).
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

export type FoilGradeBadgeSize = "xs" | "sm" | "md" | "lg" | "xl";

interface FoilGradeBadgeProps {
  /** Letter grade ("S", "A+", "B"), or numeric score string ("7.4", "10"). */
  grade: string;
  /** Optional caption rendered under the badge (uppercase tracked). */
  label?: string;
  /** Pixel size — default md (80px). */
  size?: FoilGradeBadgeSize;
  /** Extra wrapper classes (positioning). */
  className?: string;
  /** Play spring-bounce on mount. Default true. False = fade-in only. */
  animateOnMount?: boolean;
  /** Optional spring delay (seconds). Default 0. */
  animationDelay?: number;
}

/** Tier classification (handles both letters and numeric scores). */
function classifyGrade(grade: string): "high" | "midA" | "midB" | "low" {
  const trimmed = grade.trim();
  if (trimmed === "") return "low";
  const asNum = Number(trimmed);
  if (!Number.isNaN(asNum)) {
    if (asNum >= 7) return "high";
    if (asNum >= 5) return "midA";
    return "low";
  }
  const head = trimmed.charAt(0).toUpperCase();
  if (head === "S" || head === "A") return "high";
  if (head === "B") return "midA";
  if (head === "C") return "midB";
  return "low";
}

interface TierStyle {
  letterColor: string;
  ringGradient: string;
  shadowClass: string;
}

function tierStyle(grade: string): TierStyle {
  switch (classifyGrade(grade)) {
    case "high":
      return {
        letterColor: "text-emerald-300",
        ringGradient: "from-emerald-400/70 via-emerald-300/30 to-emerald-500/60",
        shadowClass: "shadow-emerald-500/30",
      };
    case "midA":
      return {
        letterColor: "text-amber-300",
        ringGradient: "from-amber-400/70 via-orange-300/30 to-amber-500/60",
        shadowClass: "shadow-amber-500/30",
      };
    case "midB":
      return {
        letterColor: "text-orange-300",
        ringGradient: "from-orange-400/70 via-amber-300/30 to-orange-500/60",
        shadowClass: "shadow-orange-500/30",
      };
    default:
      return {
        letterColor: "text-red-300",
        ringGradient: "from-red-400/70 via-rose-300/30 to-red-500/60",
        shadowClass: "shadow-red-500/30",
      };
  }
}

/** Pixel diameter for each size token. */
function dimensionPx(size: FoilGradeBadgeSize): number {
  switch (size) {
    case "xs": return 48;
    case "sm": return 60;
    case "md": return 80;
    case "lg": return 120;
    case "xl": return 160;
  }
}

/** Letter font class — shrinks for multi-char grades (A+, B-, 7.4, 10). */
function letterFontClass(size: FoilGradeBadgeSize, grade: string): string {
  const multi = grade.length >= 2;
  const long = grade.length >= 3; // "7.4", "10", "A+"... handle longer numerics
  switch (size) {
    case "xs": return multi ? "text-base" : "text-lg";
    case "sm": return multi ? "text-xl" : "text-2xl";
    case "md": return long ? "text-2xl md:text-3xl" : multi ? "text-3xl" : "text-4xl";
    case "lg": return long ? "text-4xl md:text-5xl" : multi ? "text-5xl" : "text-6xl";
    case "xl": return long ? "text-5xl md:text-6xl" : multi ? "text-6xl" : "text-7xl";
  }
}

export function FoilGradeBadge({
  grade,
  label,
  size = "md",
  className,
  animateOnMount = true,
  animationDelay = 0,
}: FoilGradeBadgeProps) {
  const reduced = useReducedMotion();
  const { letterColor, ringGradient, shadowClass } = tierStyle(grade);
  const dim = dimensionPx(size);
  const letterSize = letterFontClass(size, grade);

  const ariaLabel = label ? `Grade ${grade} — ${label}` : `Grade ${grade}`;

  // Reduced motion: skip spring entirely.
  // animateOnMount=false: fade-in only (parent already animating wrapper).
  const initial = reduced
    ? { opacity: 0 }
    : animateOnMount
    ? { scale: 0, rotate: -15, opacity: 0 }
    : { opacity: 0 };
  const animate = reduced
    ? { opacity: 1 }
    : { scale: 1, rotate: 0, opacity: 1 };
  const transition = reduced
    ? { duration: 0.25 }
    : animateOnMount
    ? { type: "spring" as const, stiffness: 220, damping: 14, delay: animationDelay }
    : { duration: 0.3, ease: "easeOut" as const, delay: animationDelay };

  return (
    <motion.div
      role="img"
      aria-label={ariaLabel}
      initial={initial}
      animate={animate}
      transition={transition}
      className={cn(
        "relative inline-flex flex-col items-center gap-1.5",
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          "relative overflow-hidden rounded-full shadow-lg ring-1 ring-white/10",
          shadowClass,
        )}
        style={{ width: dim, height: dim }}
      >
        {/* Outer holographic ring gradient */}
        <div
          className={cn(
            "absolute inset-0 rounded-full bg-gradient-to-br",
            ringGradient,
          )}
        />
        {/* Inner surface with glossy highlight */}
        <div
          className={cn(
            "absolute inset-[3px] rounded-full",
            "bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.25)_0%,_transparent_40%)]",
            "bg-surface",
          )}
        />
        {/* Conic foil shimmer — slow rotation, paused under reduced-motion */}
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-[conic-gradient(from_0deg,_rgba(255,255,255,0.10),_transparent_25%,_rgba(255,255,255,0.10)_50%,_transparent_75%,_rgba(255,255,255,0.10))]",
            !reduced && "animate-[spin_12s_linear_infinite]",
          )}
        />
        {/* Letter */}
        <div className="relative flex h-full w-full items-center justify-center">
          <span
            className={cn(
              "font-heading font-bold leading-none tabular-nums",
              letterSize,
              letterColor,
            )}
          >
            {grade}
          </span>
        </div>
      </div>

      {label && (
        <span
          aria-hidden
          className="text-[10px] font-semibold uppercase tracking-widest text-white/55"
        >
          {label}
        </span>
      )}
    </motion.div>
  );
}
